import { ReactElement } from "react";
import { Dialog, Stack, Typography } from "@mui/material";
import { useDeleteManuallyConfiguredAutonomousSystemsMutation } from "../graphql/generated/operations";
import { Close } from "../types/components/dialog";
import { ButtonStrip } from "./ButtonStrip";

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {Close} props.close
 * @param {Function} props.openSuccessSnackbar
 * @param {Function} props.setSnackbarMessage
 * @param {string[]} props.asns
 * @returns {ReactElement}
 */
export const RemoveConfAutonomousSystemDialog = ({
  isOpen,
  close,
  openSuccessSnackbar,
  setSnackbarMessage,
  asns,
}) => {
  const [, removeConfASNs] =
    useDeleteManuallyConfiguredAutonomousSystemsMutation();
  const numberOfASNsToRemove = asns.length;

  const onRemoveASNs = async () => {
    if (numberOfASNsToRemove < 1) {
      console.error("Invalid number of asns to remove.");
      close();
      return;
    }

    const result = await removeConfASNs({ asns });

    let snackbarMessage = `Removed ASN ${asns[0]}`;
    let errorMessage = `Could not remove ASN ${asns[0]}:`;
    if (numberOfASNsToRemove > 1) {
      snackbarMessage = `Removed ${numberOfASNsToRemove} ASNs`;
      errorMessage = `Could not remove ${numberOfASNsToRemove} ASNs:`;
    }

    const numberOfRemovedASNs =
      result.data?.deleteManuallyConfiguredAutonomousSystems?.affected_rows;
    if (numberOfRemovedASNs === numberOfASNsToRemove) {
      setSnackbarMessage(snackbarMessage);
      openSuccessSnackbar();
    } else {
      console.error(errorMessage, result);
    }

    close();
  };

  let title = `Confirm removal of AS Filter ${asns[0]}`;
  if (numberOfASNsToRemove > 1) {
    title = `Confirm removal of ${numberOfASNsToRemove} AS Filters`;
  }

  return (
    <Dialog open={isOpen} onClose={close}>
      <Stack
        sx={{ p: "2rem", minHeight: "15rem", justifyContent: "space-between" }}
      >
        <Typography sx={{ color: "primary.main" }} fontSize="1.25rem">
          {title}
        </Typography>
        {numberOfASNsToRemove > 1 ? (
          <Typography sx={{ fontWeight: "bold", pt: "30px", pb: "30px" }}>
            {asns.join(", ")}
          </Typography>
        ) : undefined}
        <Typography sx={{ color: "gray2.main", pb: "1rem" }}>
          You can always add {numberOfASNsToRemove > 1 ? "them" : "it"} back
          using the <br />
          {'"Add AS Filter"'} button.
        </Typography>
        <ButtonStrip
          onSecondaryButtonClick={close}
          onMainButtonClick={onRemoveASNs}
          mainButtonTitle="Remove"
          mainButtonCustomStyles={{
            backgroundColor: "red1.main",
            width: "6.5rem",
            ":hover": {
              backgroundColor: "red1.light",
            },
          }}
        />
      </Stack>
    </Dialog>
  );
};
